// TA-9295: coordination between the send path (useSendMessage) and the
// `chatUpdate` subscription handler (useChatInput) around marking a chat read.
//
// Background: `markChatsRead` used to fire only when a chat was OPENED. A
// message that arrived while the thread was already open was marked read only
// if the per-chat `messageUpdate` subscription delivered it (ChatMessageData's
// onData → debouncedUpdateChatsRead), and that subscription is known to miss
// events (TA-10150). The unread then stayed unread server-side, so replying
// left a row reading "You: 👍" with a green "1" badge that survived every
// refetch. Sending from the thread composer now marks the chat read.
//
// Two things have to hold for that to actually fix the badge:
//
// 1. Every send must end up covered by a markChatsRead. Bursts still must not
//    fire one request per message (TA-9951, Sentry Consecutive HTTP), so sends
//    are COALESCED rather than dropped: while a request for a chat is in
//    flight, further sends set a pending flag and exactly one follow-up runs
//    when it settles. A time-based throttle would instead silently skip the
//    receipt for a message that arrived between two rapid sends, leaving the
//    cache at 0 while the server still counts 1 — the very symptom of
//    TA-9295, reappearing on the next refetch.
// 2. The sender's own `chatUpdate` echo can be computed server-side BEFORE
//    that markChatsRead commits, so it carries the pre-read count and would
//    write the badge straight back into the cache. `shouldIgnoreUnreadUpdate`
//    suppresses the raise for the duration of the round trip plus a short
//    slack for events already queued behind it.
//
// The suppression window is deliberately tied to the request, not to a fixed
// long timer, and is never keyed on message content: a body comparison looks
// tempting (the payload carries `messageBody`) but "👍", "ok" and "thanks"
// repeat constantly in group chats, so it would swallow real badges.
//
// QA round 2 (TA-9295): a blanket window is still too wide once the receipt
// has COMMITTED. Send, leave the thread, and have someone else send while the
// slack was running, and that genuinely new message's unreadCount was pinned
// to 0 with nothing left to re-raise it until a refetch — a hidden badge, the
// mirror image of the bug this guard exists for. The slack now suppresses
// only events the receipt already covers, decided by timestamp:
//
//   markChatsRead returns the chat's `recentActivityAt` as of the commit —
//   the "read through" watermark. Both it and the `chatUpdate` payload's
//   `recentActivityAt` come from the server clock, so they are directly
//   comparable (no device-clock skew, unlike the optimistic value the send
//   path writes into the cache). An event at or before the watermark was
//   computed against pre-receipt state → suppress. An event after it is a
//   message the receipt did not cover → apply it, badge and all.
//
// In flight, there is no watermark yet and the response ordering between the
// HTTP mutation and the WS event says nothing, so suppression there stays
// unconditional — that window is one request round trip, and it is where our
// own echo almost always lands. But that round trip IS the badge-hiding
// window on a slow connection (seconds, on weak mobile data), so the decision
// is not dropped, only DEFERRED: an event suppressed in flight is stashed,
// and once the watermark arrives it is judged by the same rule and restored
// if it postdates the receipt. Writing 0 first and restoring after keeps the
// common case — our own echo — free of a badge flash.

// Slack after a markChatsRead resolves during which a `chatUpdate` for that
// chat is still assumed to predate the read receipt. Covers events already on
// the wire when the mutation committed.
export const READ_SUPPRESSION_SLACK_MS = 1_500;

type InFlight = { pendingRun: (() => Promise<unknown>) | null };
type Settled = {
  /** Epoch ms after which nothing is suppressed for this chat. */
  until: number;
  /**
   * Server `recentActivityAt` (epoch ms) the receipt read through, or null
   * when the response did not carry one — an older backend, a partial error,
   * or a caller whose mutation selects no `recentActivityAt`. Null falls back
   * to suppressing the whole slack window, i.e. the pre-QA behaviour: never
   * let a missing field reopen the badge-flash this guard prevents.
   */
  readThroughMs: number | null;
};

/** A `chatUpdate` suppressed in flight, kept until the watermark can judge it. */
type StashedUpdate = {
  recentActivityAt?: string | null;
  unreadCount?: number | null;
};

const inFlightByChat = new Map<string, InFlight>();
const settledByChat = new Map<string, Settled>();
// Latest in-flight-suppressed event per chat. Only the latest matters: the
// server recomputes the count for every event, so a newer one supersedes.
const stashedByChat = new Map<string, StashedUpdate>();
let restoreHandler: ((chatId: string, unreadCount: number) => void) | null =
  null;
// Bumped by resetChatReadGuards. Clearing the maps cannot cancel a request
// already on the wire: without this, a receipt that resolves after logout
// still writes its watermark, its failure record and its queued follow-up
// into the NEXT session — and for a chat id both accounts can see (a shared
// chat), that suppresses the new user's badges. Every completion carries the
// generation it started in and does nothing once that generation is stale.
let generation = 0;
// Chats whose last receipt request rejected. The send path re-issues one the
// next time it runs for that chat, which is what covers a message composed
// offline: `useChatProcessing` re-invokes sendDraftMessage once the device is
// back online, long after RetryLink gave up on the original mutation.
const failedReceiptChats = new Set<string>();

/**
 * Pulls the read-through watermark out of a markChatsRead result. Shaped
 * against `MarkChatsReadMutation` (`markChatsRead` is an array of chats), but
 * typed loosely on purpose: the coalescer takes an opaque `() => Promise`
 * from three call sites and must not break if one of them ever resolves to
 * something else — it degrades to the blanket window instead.
 */
const readThroughMsFromResult = (
  chatId: string,
  result: unknown
): number | null => {
  const chats = (result as { data?: { markChatsRead?: unknown } } | null)?.data
    ?.markChatsRead;
  if (!Array.isArray(chats)) return null;
  const chat = chats.find(
    (item: { id?: string } | null) => item?.id === chatId
  ) as { recentActivityAt?: string | null } | undefined;
  if (typeof chat?.recentActivityAt !== 'string') return null;
  const ms = Date.parse(chat.recentActivityAt);
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Judges the event stashed while the receipt was in flight, now that the
 * watermark is known: an event the receipt did not read through is a real
 * message from someone else, so its count goes back into the cache.
 *
 * Restores only a positive count, and the handler raises rather than assigns
 * — by the time this runs the user may have opened the chat, or a newer event
 * may have landed, and neither should be undone by a stale value.
 */
const restoreStashedUnread = (
  chatId: string,
  readThroughMs: number | null
): void => {
  const stashed = stashedByChat.get(chatId);
  if (!stashed) return;
  stashedByChat.delete(chatId);
  // No watermark: the event cannot be placed relative to the receipt, so the
  // blanket suppression stands, exactly as in the slack window.
  if (readThroughMs === null) return;
  const { recentActivityAt, unreadCount } = stashed;
  if (!unreadCount || unreadCount <= 0) return;
  const eventMs = recentActivityAt ? Date.parse(recentActivityAt) : NaN;
  if (Number.isNaN(eventMs) || eventMs <= readThroughMs) return;
  restoreHandler?.(chatId, unreadCount);
};

const startRun = (chatId: string, run: () => Promise<unknown>): void => {
  const entry: InFlight = { pendingRun: null };
  const startedIn = generation;
  inFlightByChat.set(chatId, entry);
  let failed = false;
  let readThroughMs: number | null = null;
  // An async IIFE, not `Promise.resolve(run())`: the body runs synchronously
  // up to the implicit await, so the receipt still leaves the device as soon
  // as the message does, but a synchronous throw from `run` becomes a
  // rejection the catch below handles rather than escaping to the caller.
  // (A try/catch around a promise would not catch the rejection — S4822.)
  (async () => run())()
    .then((result) => {
      readThroughMs = readThroughMsFromResult(chatId, result);
    })
    .catch(() => {
      // Non-fatal here: the chat is recorded below so the next send for it
      // re-issues the receipt, and re-opening the chat fires markChatsRead
      // anyway. RetryLink covers transient network failures before this.
      failed = true;
    })
    .then(() => {
      // Logout (or any other reset) happened while this was in flight: the
      // session it belonged to is gone, so none of its bookkeeping applies.
      if (startedIn !== generation) return;
      inFlightByChat.delete(chatId);
      if (failed) failedReceiptChats.add(chatId);
      else failedReceiptChats.delete(chatId);
      settledByChat.set(chatId, {
        until: Date.now() + READ_SUPPRESSION_SLACK_MS,
        readThroughMs,
      });
      // The follow-up runs the LAST queued caller's closure, not this one's.
      // The callers are not interchangeable: ChatMessageData's carries an
      // onCompleted that schedules the TA-10379 catch-up fetch, the send
      // path's does not. Re-running this one would drop that side effect on
      // the floor (and, in the other ordering, fire it twice).
      if (entry.pendingRun) {
        // Keep the stash: a queued receipt is about to produce a newer
        // watermark, and that is the one the event deserves to be judged by.
        startRun(chatId, entry.pendingRun);
        return;
      }
      restoreStashedUnread(chatId, readThroughMs);
    });
};

/**
 * Runs `run` (the markChatsRead mutation) for `chatId`, coalescing concurrent
 * requests: at most one is in flight per chat, and at most one follow-up is
 * queued behind it — the most recent caller's, so no caller's side effects
 * are lost. Suppression of incoming unread counts starts immediately and
 * lasts until READ_SUPPRESSION_SLACK_MS after the last request settles.
 */
export const requestChatMarkedRead = (
  chatId: string | null | undefined,
  run: () => Promise<unknown>
): void => {
  if (!chatId) return;
  // Drop an expired entry here rather than in the predicate, so reading the
  // guard stays free of side effects. Entries are bounded by the number of
  // chats touched in a session and cleared on logout.
  const settled = settledByChat.get(chatId);
  if (settled !== undefined && Date.now() >= settled.until) {
    settledByChat.delete(chatId);
  }
  const entry = inFlightByChat.get(chatId);
  if (entry) {
    entry.pendingRun = run;
    return;
  }
  startRun(chatId, run);
};

/**
 * True when a receipt request for `chatId` failed and has not been replaced
 * by a successful one. The send path uses this to re-issue the mutation for a
 * message that was composed offline and only reached the server later.
 */
export const hasFailedChatReadReceipt = (
  chatId: string | null | undefined
): boolean => !!chatId && failedReceiptChats.has(chatId);

/**
 * True when a `chatUpdate` payload's unreadCount must not raise the badge:
 * this device's own markChatsRead for `chatId` is in flight, or it settled
 * within the slack window AND the event is one the receipt already covered
 * (its `recentActivityAt` is at or before the read-through watermark).
 *
 * @param update.recentActivityAt the payload's server timestamp. Omitted or
 * unparseable, the event cannot be placed relative to the receipt, so the
 * window suppresses it as before.
 * @param update.now injectable clock for tests.
 */
export const shouldIgnoreUnreadUpdate = (
  chatId: string | null | undefined,
  update: { recentActivityAt?: string | null; now?: number } = {}
): boolean => {
  if (!chatId) return false;
  if (inFlightByChat.has(chatId)) return true;

  const { recentActivityAt, now = Date.now() } = update;
  const settled = settledByChat.get(chatId);
  if (settled === undefined || now >= settled.until) return false;
  if (settled.readThroughMs === null) return true;

  const eventMs = recentActivityAt ? Date.parse(recentActivityAt) : NaN;
  if (Number.isNaN(eventMs)) return true;
  // Strictly after the watermark → activity the receipt did not read through,
  // i.e. someone else sent while the slack was running. Let the badge show.
  return eventMs <= settled.readThroughMs;
};

/**
 * Stashes a `chatUpdate` the caller suppressed, so the badge it would have
 * raised is not lost when the suppression turns out to have been wrong.
 *
 * Only in-flight suppression is deferrable: inside the slack window the
 * watermark has already judged the event, and the blanket fallback there has
 * no watermark to judge it by later either. A no-op otherwise, so callers can
 * hand over every suppressed event without checking which window they are in.
 */
export const stashSuppressedUnreadUpdate = (
  chatId: string | null | undefined,
  update: { recentActivityAt?: string | null; unreadCount?: number | null }
): void => {
  if (!chatId || !inFlightByChat.has(chatId)) return;
  stashedByChat.set(chatId, update);
};

/**
 * Registers what to do with a restored count — writing it to the Apollo cache
 * lives with the subscription handler, not here. Pass null to unregister.
 * Replacing an existing handler is deliberate: the chatUpdate subscription is
 * mounted once, and a re-subscribe must own the restore.
 */
export const setSuppressedUnreadRestoreHandler = (
  handler: ((chatId: string, unreadCount: number) => void) | null
): void => {
  restoreHandler = handler;
};

/**
 * True only when the last receipt that settled for `chatId` carried a
 * watermark and `recentActivityAt` is strictly after it — i.e. the event is
 * activity the receipt provably did not read. Unlike shouldIgnoreUnreadUpdate
 * this does not stop at the slack window: the settled entry outlives it until
 * the next receipt for the chat replaces it.
 *
 * False with no settled receipt, no watermark, or an unparseable timestamp:
 * a caller using this to justify raising a badge must fail closed.
 */
export const isAfterReadThrough = (
  chatId: string | null | undefined,
  recentActivityAt: string | null | undefined
): boolean => {
  if (!chatId) return false;
  const readThroughMs = settledByChat.get(chatId)?.readThroughMs;
  if (readThroughMs === undefined || readThroughMs === null) return false;
  const eventMs = recentActivityAt ? Date.parse(recentActivityAt) : Number.NaN;
  return !Number.isNaN(eventMs) && eventMs > readThroughMs;
};

/**
 * Clears every guard. Called on logout — this is module state, and it would
 * otherwise outlive the session and suppress a badge for the next account
 * signed in on the same device (same class of bug as TA-10160).
 */
export const resetChatReadGuards = (): void => {
  generation += 1;
  inFlightByChat.clear();
  settledByChat.clear();
  failedReceiptChats.clear();
  stashedByChat.clear();
  restoreHandler = null;
};
